import {
  ClockCircleOutlined,
  DeleteOutlined,
  GlobalOutlined,
  LockOutlined,
  PlayCircleOutlined,
  YoutubeOutlined,
} from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Button, Image, Popconfirm, Table, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import type React from "react"
import { Link, useNavigate } from "react-router-dom"
import { TABLE_ACTION_CELL_CLASSNAME } from "@/constants/table"
import { useTableScroll } from "@/hooks/useTableScroll"
import type { Video } from "@/services/video/videoApi"
import { formatVideoTime } from "../utils"
import "@/styles/table.full.css"

const { Text } = Typography

interface VideoTableProps {
  data: Video[]
  loading: boolean
  onDelete: (id: string) => void
  pagination?: TablePaginationConfig
}

const statusTagMap: Record<string, { color: string; label: string }> = {
  published: { color: "success", label: "Đã xuất bản" },
  draft: { color: "warning", label: "Bản nháp" },
  archived: { color: "default", label: "Đã lưu trữ" },
}

export const VideoTable: React.FC<VideoTableProps> = ({ data, loading, onDelete, pagination }) => {
  const navigate = useNavigate()
  const { scrollConfig, tableWrapperRef } = useTableScroll(1250)

  const columns: TableProps<Video>["columns"] = [
    {
      title: "#ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 75,
      align: "center",
      render: (value: string) => <span className="font-semibold text-gray-500">#{value}</span>,
    },
    {
      title: "Video YouTube",
      key: "video",
      width: 380,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="relative shrink-0 overflow-hidden rounded-md border border-gray-200 shadow-xs">
            {record.image ? (
              <Image
                src={record.image}
                alt={record.name}
                width={100}
                height={56}
                className="object-cover"
                fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='56' fill='%23e5e7eb'><rect width='100%' height='100%'/></svg>"
                preview={false}
              />
            ) : (
              <div className="flex h-14 w-25 items-center justify-center bg-gray-100 text-gray-400">
                <YoutubeOutlined className="text-2xl" />
              </div>
            )}
            <span className="absolute right-1 bottom-1 rounded bg-black/75 px-1 py-0.5 font-medium text-[10px] text-white">
              {formatVideoTime(record.duration)}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <Link
              to={`/videos/${record.id}`}
              className="line-clamp-2 font-medium text-gray-800 transition-colors hover:text-blue-600"
              title={record.name}
            >
              {record.name}
            </Link>
            <div className="mt-1 flex items-center gap-2 text-gray-400 text-xs">
              <span className="inline-flex items-center gap-1 font-mono text-gray-500">
                <YoutubeOutlined className="text-red-500" />
                {record.source_id}
              </span>
              <span>•</span>
              <span>{record.segment_count} câu thoại</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Thời lượng",
      dataIndex: "duration",
      key: "duration",
      width: 120,
      align: "center",
      render: (value: number) => (
        <Tag icon={<ClockCircleOutlined />} color="blue">
          {formatVideoTime(value)}
        </Tag>
      ),
    },
    {
      title: "Số đoạn",
      dataIndex: "segment_count",
      key: "segment_count",
      width: 110,
      align: "center",
      render: (value: number) => <Tag color="cyan">{value} đoạn</Tag>,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 130,
      align: "center",
      render: (status: string) => {
        const item = statusTagMap[status] || { color: "default", label: status }
        return <Tag color={item.color}>{item.label}</Tag>
      },
    },
    {
      title: "Truy cập",
      dataIndex: "is_public",
      key: "is_public",
      width: 120,
      align: "center",
      render: (value: boolean) =>
        value ? (
          <Tag icon={<GlobalOutlined />} color="green">
            Công khai
          </Tag>
        ) : (
          <Tag icon={<LockOutlined />} color="default">
            Riêng tư
          </Tag>
        ),
    },
    {
      title: "Người tạo",
      key: "creator",
      width: 150,
      render: (_, record) => {
        const name = record.creator?.full_name || record.creator?.username
        return name ? (
          <Text className="text-sm">{name}</Text>
        ) : (
          <Text type="secondary" className="text-xs">
            —
          </Text>
        )
      },
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (value: string) => (
        <span className="text-gray-500 text-xs">{value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—"}</span>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      fixed: "right",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div className={TABLE_ACTION_CELL_CLASSNAME}>
          <Tooltip title="Xem & Dịch phụ đề">
            <Button
              type="text"
              size="small"
              icon={<PlayCircleOutlined className="text-base text-blue-500" />}
              onClick={() => navigate(`/videos/${record.id}`)}
            />
          </Tooltip>

          <Tooltip title="Xóa video">
            <Popconfirm
              title="Xóa video"
              description="Bạn có chắc chắn muốn xóa video này cùng toàn bộ phụ đề?"
              onConfirm={() => onDelete(record.id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined className="text-base" />} />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ]

  return (
    <div ref={tableWrapperRef} className="full-height-table grow overflow-hidden">
      <Table<Video>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        scroll={scrollConfig}
        size="middle"
        bordered
      />
    </div>
  )
}
